import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Trophy, TrendingUp, TrendingDown, Minus, ChevronRight, Zap, Target } from 'lucide-react'
import type { LeaderboardEntry } from '@/lib/jira/metrics'
import { getPerformanceLevel, getPerformanceLevelConfig } from '@/lib/jira/metrics'

interface JiraTeamLeaderboardProps {
  data: LeaderboardEntry[]
  title?: string
  loading?: boolean
  className?: string
  maxItems?: number
  compact?: boolean
}

const RankBadge = memo(function RankBadge({ rank, badge }: { rank: number; badge?: 'gold' | 'silver' | 'bronze' }) {
  if (badge === 'gold') {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center shadow-md">
        <Trophy className="h-4 w-4 text-white" />
      </div>
    )
  }
  if (badge === 'silver') {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center shadow-md">
        <span className="text-sm font-bold text-white">2</span>
      </div>
    )
  }
  if (badge === 'bronze') {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center shadow-md">
        <span className="text-sm font-bold text-white">3</span>
      </div>
    )
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{rank}</span>
    </div>
  )
})

const TrendIndicator = memo(function TrendIndicator({ trend }: { trend: 'up' | 'down' | 'stable' }) {
  if (trend === 'up') {
    return <TrendingUp className="h-4 w-4 text-green-500" />
  }
  if (trend === 'down') {
    return <TrendingDown className="h-4 w-4 text-red-500" />
  }
  return <Minus className="h-4 w-4 text-gray-400" />
})

const ProgressBar = memo(function ProgressBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${value}%` }}
      />
    </div>
  )
})

const LeaderboardItem = memo(function LeaderboardItem({
  entry,
  compact = false,
}: {
  entry: LeaderboardEntry
  compact?: boolean
}) {
  const navigate = useNavigate()
  const level = getPerformanceLevel(entry.overallScore)
  const levelConfig = getPerformanceLevelConfig(level)

  const handleClick = () => {
    navigate(`/jira/person/${entry.user.accountId}`)
  }

  const progressColor = entry.overallScore >= 75 ? 'bg-green-500' :
    entry.overallScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'

  if (compact) {
    return (
      <div
        onClick={handleClick}
        className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg cursor-pointer transition-colors group"
      >
        <RankBadge rank={entry.rank} badge={entry.badge} />
        
        {entry.user.avatarUrl ? (
          <img
            src={entry.user.avatarUrl}
            alt={entry.user.displayName}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
            {entry.user.displayName.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {entry.user.displayName}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-gray-900 dark:text-white">
            {entry.overallScore}
          </span>
          <TrendIndicator trend={entry.trend} />
        </div>

        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200" />
      </div>
    )
  }

  return (
    <div
      onClick={handleClick}
      className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md cursor-pointer transition-all group"
    >
      <RankBadge rank={entry.rank} badge={entry.badge} />

      <div className="flex-shrink-0">
        {entry.user.avatarUrl ? (
          <img
            src={entry.user.avatarUrl}
            alt={entry.user.displayName}
            className="w-12 h-12 rounded-full ring-2 ring-white dark:ring-gray-700 shadow-md"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold shadow-md">
            {entry.user.displayName.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="font-semibold text-gray-900 dark:text-white truncate">
            {entry.user.displayName}
          </p>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${levelConfig.bgColor} ${levelConfig.color}`}>
            {levelConfig.label}
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <Target className="h-3.5 w-3.5" />
            <span>%{entry.completionRate} tamamlama</span>
          </div>
          <div className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" />
            <span>{entry.velocity}/hafta</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {entry.overallScore}
          </span>
          <TrendIndicator trend={entry.trend} />
        </div>
        <ProgressBar value={entry.overallScore} color={progressColor} />
      </div>

      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-colors" />
    </div>
  )
})

const LoadingState = memo(function LoadingState({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
      ))}
    </div>
  )
})

const EmptyState = memo(function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
      <Trophy className="h-12 w-12 mb-3" />
      <p className="text-sm">Henüz veri bulunamadı</p>
    </div>
  )
})

const JiraTeamLeaderboard = memo(function JiraTeamLeaderboard({
  data,
  title = 'Takım Performans Sıralaması',
  loading = false,
  className = '',
  maxItems = 10,
  compact = false,
}: JiraTeamLeaderboardProps) {
  const displayData = data.slice(0, maxItems)

  return (
    <Card variant="default" padding="md" className={className}>
      <CardHeader compact>
        <CardTitle compact className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <span>{title}</span>
          </div>
          {data.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              {data.length} kişi
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent compact>
        {loading ? (
          <LoadingState count={compact ? 3 : 5} />
        ) : displayData.length === 0 ? (
          <EmptyState />
        ) : compact ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {displayData.map((entry) => (
              <LeaderboardItem key={entry.user.accountId} entry={entry} compact />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {displayData.map((entry) => (
              <LeaderboardItem key={entry.user.accountId} entry={entry} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
})

export default JiraTeamLeaderboard

